/**
 * The editor's in-memory view of a floorplan.
 *
 * Deliberately separate from the plain `dashboard/models` classes the booking viewer uses.
 * Those model what the API happens to return, with every field optional and no defaults;
 * the editor needs every property present so a component can read `rotation` without
 * guarding first. `normalizeFloorPlan` is what turns one into the other, and it is the only
 * place a default is applied.
 */

export type ElementKind = 'desk' | 'deskShape' | 'textLabel' | 'iconLabel' | 'polygon';

/**
 * What a drawn polygon is, when it is more than an area.
 *
 * Stored in `elementType`, which is the only property besides `featureId` that the mobile
 * app's `vector_map` passes through for the floor layer — so it is the only place the
 * distinction can live and still survive a reload. Every consumer tests that property for
 * equality with `"desk"`, so any other value renders exactly as an untagged polygon always
 * has; the editor is what draws a wall as a wall.
 */
export type StructuralType = 'wall' | 'wall_ext' | 'wall_partition' | 'door' | 'window' | 'glazing' | 'furniture'
  | 'stairs' | 'elevator' | 'fixture' | 'drawing';

export const STRUCTURAL_TYPES: readonly StructuralType[] = [
  'wall',
  'wall_ext',
  'wall_partition',
  'door',
  'window',
  'glazing',
  // Not structure, but the one other answer to "what is this": furniture that is not a desk.
  // Set by "not a desk", so the server's desk tagger stops turning it back into one.
  'furniture',
  // Getting between floors: a staircase, a lift shaft.
  'stairs',
  'elevator',
  // A fitting plumbed or wired in: a toilet, a washbasin, a bath, a shower tray, a hob.
  'fixture',
  // Part of the drawing a plan was traced from, kept exactly as drawn (`AS_DRAWN_ELEMENT_TYPE`).
  'drawing',
];

/**
 * A piece of the drawing a plan was traced from that nobody marked as anything — a meeting table
 * with its chairs, a curved wall, writing — kept as the outline of its ink. A floor shape like
 * any other, to select, move, resize or remove, and to say what it is; drawn in ink until then.
 */
export const AS_DRAWN_ELEMENT_TYPE: StructuralType = 'drawing';

/** Whether a floor shape is kept as drawn from a traced picture, and nobody has said what it is. */
export const isAsDrawn = (feature: { readonly kind: string; readonly elementType?: StructuralType | null }): boolean => (
  feature.kind === 'polygon' && feature.elementType === AS_DRAWN_ELEMENT_TYPE);

/** The types that are walls of one kind or another. */
export const WALL_ELEMENT_TYPES: readonly StructuralType[] = ['wall', 'wall_ext', 'wall_partition'];

/** Which of the three polygon layers a drawn shape belongs to. */
export type PolygonLayer = 'border' | 'room' | 'floor';

export type DeskType = 'single' | 'pod';

export type LabelKind = 'text' | 'icon';

/** Local drawing units. Not longitude/latitude, and not metres — every project has its own arbitrary scale. */
export type Coordinate = readonly [number, number];

/** MultiPolygon nesting: polygon → ring → vertex. */
export type MultiPolygonCoordinates = Coordinate[][][];

export interface EditorFeatureBase {
  /**
   * The editor's handle on this feature. Stable across an edit; regenerated only for a newly
   * created feature.
   *
   * Guaranteed unique within its kind, which the stored `id` is not — fifteen floorplans give
   * every floor feature the zero UUID, and selection, hover and React keying are all
   * `${kind}:${id}`, so duplicates would make a whole layer behave as one object. See
   * `uniqueIdentifiers` in `normalize-floorplan`.
   */
  readonly id: string;
  /**
   * The `id` exactly as the stored GeoJSON carried it, written straight back on save.
   *
   * Kept apart from [id] so that giving the editor a usable handle never rewrites an
   * identifier the editor did not create. Equal to [id] for a feature the editor added.
   */
  readonly storedId: string;
  /**
   * For a desk this is the `Workplaces.Id` that makes it bookable; for a room polygon it is
   * the `Rooms.Id`. For anything the editor creates it is a fresh id with no row behind it.
   */
  readonly featureId: string;
  /** Every property the editor does not model, preserved so a save never drops it. */
  readonly unknownProperties: Readonly<Record<string, unknown>>;
}

export interface DeskFeature extends EditorFeatureBase {
  readonly kind: 'desk';
  readonly coordinates: Coordinate;
  /** Degrees clockwise. Display-only: rotating a desk never moves its point. */
  readonly rotation: number;
  readonly deskType: DeskType;
  /** Non-null only for a pod member. Desks sharing one move and rotate together. */
  readonly podId: string | null;
  /**
   * An MDI icon to draw this desk as, instead of the plain marker.
   *
   * Editor-side only for now, and deliberately stored anyway: the mobile app draws every
   * workplace as a coloured circle from its own theme and ignores this, but the property
   * survives a save, so the plan is ready the day the app reads it rather than needing every
   * desk re-tagged then.
   */
  readonly iconName: string | null;
  /**
   * Seats on this desk. Always 1 for anything the editor creates, and not editable: a
   * workplace is one seat, and a room's capacity is what counts several.
   *
   * Kept on the model only so a stored value survives a round-trip. Nothing reads it —
   * the backend just defaults it, the mobile app does not model it — and two desk features
   * in the whole database carry anything other than 1.
   */
  readonly capacity: number;
  /**
   * The `Rooms.Id` this desk's workplace belongs to, or null when it belongs to none.
   *
   * A desk is bookable because a `Workplaces` row exists for its `featureId`, and that row
   * has to sit in a room. Null therefore means "drawn but not bookable" — either a desk the
   * editor has just placed, or one of the desks in the stored data with no workplace behind
   * it at all.
   */
  readonly roomId: string | null;
  /**
   * How big this desk's marker or icon is drawn, as a multiple of the standard size. Absent
   * means 1, which is every desk nobody has resized.
   *
   * Display only, like the icon: it changes nothing about the workplace, and the mobile app
   * draws its own fixed-size marker regardless. Stored so the editor shows the plan the way it
   * was laid out — a desk icon enlarged to read on a busy floor stays enlarged after a reload.
   */
  readonly iconScale?: number;
}

/** The bounds of a desk's icon size: small enough to read as a dot, big enough to find. */
export const MIN_DESK_ICON_SCALE = 0.5;
export const MAX_DESK_ICON_SCALE = 4;

export interface DeskShapeFeature extends EditorFeatureBase {
  readonly kind: 'deskShape';
  readonly coordinates: MultiPolygonCoordinates;
  /**
   * Degrees clockwise. Unlike every other rotation here this one is bookkeeping for a real
   * vertex rotation — the drawn shape always comes from the actual coordinates.
   */
  readonly rotation: number;
  /**
   * An MDI icon to draw this piece of furniture as, instead of its outline.
   *
   * The outline is kept either way, and is still what is stored: it is the footprint, it is
   * what the desk detector reads, and it is what a client that knows nothing about icons
   * falls back to drawing.
   */
  readonly iconName: string | null;
}

export interface TextLabelFeature extends EditorFeatureBase {
  readonly kind: 'textLabel';
  readonly coordinates: Coordinate;
  readonly text: string;
  readonly fontSize: number;
  readonly color: string;
  readonly rotation: number;
}

export interface IconLabelFeature extends EditorFeatureBase {
  readonly kind: 'iconLabel';
  readonly coordinates: Coordinate;
  /** A raw MDI slug with no `mdi-` prefix, e.g. `fire-extinguisher`. */
  readonly iconName: string | null;
  readonly iconSize: number;
  readonly color: string;
  readonly rotation: number;
}

/**
 * A drawn area: the plan's outline, a room zone, or a wall, door, window or plain shape on
 * the floor layer.
 *
 * One kind for all three layers because the geometry and the editing are identical — what
 * differs is which column it is written back to, which [layer] records. Desk shapes stay a
 * kind of their own: they are matched to a desk by the server's tagger and merged rather
 * than replaced, and nothing about that applies to a wall.
 */
export interface PolygonFeature extends EditorFeatureBase {
  readonly kind: 'polygon';
  readonly layer: PolygonLayer;
  /**
   * The nesting the stored feature declared, so a save writes it back in the shape it
   * arrived in.
   *
   * 23 stored room features are plain `Polygon`s rather than `MultiPolygon`s and render
   * correctly today. They are held here as single-polygon MultiPolygons so the renderer has
   * one shape to handle, and this is what stops the save silently rewriting bytes nobody
   * asked to change.
   */
  readonly geometryType: 'Polygon' | 'MultiPolygon';
  readonly coordinates: MultiPolygonCoordinates;
  /**
   * Degrees clockwise. Bookkeeping for a real vertex rotation, exactly as on a desk shape —
   * the drawn area always comes from the actual coordinates.
   */
  readonly rotation: number;
  /** What this shape is, or null for a plain area. Only ever set on the floor layer. */
  readonly elementType: StructuralType | null;
  /**
   * The centreline this was generated from, for a wall, door or window.
   *
   * A structural element is drawn as a line with a thickness and stored as the rectangle
   * that produces. Keeping the line means the thickness stays editable afterwards; without
   * it, changing a wall from 200mm to 300mm means deleting it and drawing it again. Null for
   * anything drawn directly as an area, including a structural element imported from CAD.
   */
  readonly centreline: readonly [Coordinate, Coordinate] | null;
  /** Metres, and only meaningful alongside [centreline]. */
  readonly thickness: number | null;
}

export type EditorFeature = DeskFeature | DeskShapeFeature | TextLabelFeature | IconLabelFeature | PolygonFeature;

/**
 * A feature whose `coordinates` are a MultiPolygon rather than a single point.
 *
 * Worth a name because the distinction drives every piece of geometry code here — moving,
 * rotating, scaling, hit-testing and framing all branch on it — and the two kinds that have
 * it are otherwise unrelated. Testing `kind === 'deskShape'` used to be the same question by
 * accident; now that a wall is a polygon too, it is not, and a missed site would read a ring
 * of rings as an `[x, y]` pair and produce NaN geometry without throwing.
 */
export type AreaFeature = DeskShapeFeature | PolygonFeature;

export const isAreaFeature = (feature: EditorFeature): feature is AreaFeature => (
  feature.kind === 'deskShape' || feature.kind === 'polygon'
);

/** A locked feature: drawn as backdrop, never selectable. */
export interface BackdropFeature {
  readonly id: string;
  readonly coordinates: MultiPolygonCoordinates;
}

/**
 * Locked backdrop drawn as lines rather than filled shapes.
 *
 * Most plans describe their walls as polygons, but not all: BDO Maastricht's ground floor
 * holds its entire floor drawing in a single `MultiLineString`, and skipping it left that
 * plan with rooms and an outline but nothing inside them.
 */
export interface LineBackdropFeature {
  readonly id: string;
  /** One entry per line, each a run of points to be stroked in order. */
  readonly paths: Coordinate[][];
}

export type LayerName = 'border' | 'floor' | 'room' | 'workplaces' | 'label';

export interface EditableFloorPlan {
  readonly floorPlanId: string;
  readonly officeId: string;
  readonly storey: number;
  /**
   * When the server last wrote this plan, as it was loaded.
   *
   * Returned on save so the server can refuse an edit made against a copy someone else has
   * since replaced. Undefined for a plan loaded from a response that did not carry it, in
   * which case the save behaves as it always did: last write wins.
   */
  readonly lastUpdatedAt?: string;
  /**
   * When the plan was first published, or null while it never has been: until then only the
   * editor sees it, not the app or the check-in screen. Undefined from a server that predates
   * publishing, where every plan is live.
   */
  readonly publishedAt?: string | null;
  /**
   * How many drawing units make one metre, or null when nobody has measured this plan.
   *
   * Null is the normal case — every stored plan predates it — and is deliberately not
   * defaulted. A guessed scale makes every area, thickness and detection figure confidently
   * wrong; an absent one makes them visibly missing, which an admin can act on.
   */
  readonly unitsPerMetre: number | null;
  readonly border: PolygonFeature[];
  readonly rooms: PolygonFeature[];
  /** Floor features that are not tagged desk shapes: walls and structural fragments. */
  readonly floorBackdrop: PolygonFeature[];
  /** The same, for the floor features drawn as lines instead of filled shapes. */
  readonly floorLines: LineBackdropFeature[];
  readonly desks: DeskFeature[];
  readonly deskShapes: DeskShapeFeature[];
  readonly labels: Array<TextLabelFeature | IconLabelFeature>;
  /**
   * Whatever the stored layers carried around their features — `type`, `name`, and any other
   * top-level key. Kept so a save can rebuild each collection exactly as it was found.
   */
  readonly layerEnvelopes: Readonly<Record<LayerName, Readonly<Record<string, unknown>>>>;
}

export const DEFAULT_LABEL_COLOR = '#1a1a19';
export const DEFAULT_FONT_SIZE = 14;
export const DEFAULT_ICON_SIZE = 24;
